import React from 'react';
import { Box, Typography } from "@mui/material";
import { HelpBox } from "@/components/atoms";
import {
  ActivePage,
  Audience,
  AudienceSegment,
  AudienceSummary,
  AudienceTravelTime,
  AudienceType,
  Breadcrumb,
  BuildAudience,
  GeoFenceLocation,
  UploadAudience,
  Variable,
  VariableGroup
} from "@/types";
import { ageOptions, incomeOptions, uploadModeOptions } from "./constants";

const client = process.env.REACT_APP_CLIENT || "core";

export const defaultAudience: Audience = {
  audience_id: "",
  audience_name: "",
  client: client,
  created_at: "",
  segment: false,
  segments: [],
  audience_type: "build",
  audience_geo: "cube",
  build_mode: "balanced",
  operator: "AND",
  var_groups: [{ id: 1, variables: [], operator: "AND" }],
  export: true,
  project_name: null,
  shared: false,
  status: "idle",
};

export const defaultBuildAudience: BuildAudience = {
  audience_id: "",
  audience_name: "",
  client: client,
  created_at: "",
  segment: false,
  segments: [],
  audience_type: "build",
  audience_geo: "cube",
  build_mode: "balanced",
  operator: "AND",
  var_groups: [{ id: 1, variables: [], operator: "AND" }],
  export: true,
  project_name: null,
  shared: false,
  status: "idle",
};

export const defaultUploadAudience: UploadAudience = {
  audience_id: "",
  audience_name: "",
  client: client,
  created_at: "",
  segment: false,
  segments: [],
  audience_type: "upload",
  audience_geo: "postcode",
  upload_file: null,
  export: true,
  project_name: null,
  shared: false,
  status: "idle",
};

export const defaultTravelTime: AudienceTravelTime = {
  mode: 'combined',
  transportation: 'driving',
  duration: 15,
  departure_day: 'weekday',
  departure_time: 'morning'
}


export const defaultVariableGroup = (id: number) => {
  return {
    id: id,
    variables: [],
    operator: "AND",
  };
};

/**
 * Build breadcrumbs for an audience page
 */
export function getAudienceBreadcrumb(
  activePage: ActivePage | null,
  audiencePath: string,
  audience: Audience | null,
  loading: boolean,
): Breadcrumb[] {

  const ROUTE_LABELS: Record<string, string> = {
    'upload': 'Uploads',
    'insight': 'Insights',
    'export': 'Export',
    'new': 'New'
  };

  if (!activePage) return [];
  if (loading) return activePage.breadcrumbs;
  const breadcrumbs = [ ...activePage.breadcrumbs ];

  // Extract the parts of the path and convert to a breadcrumb
  const paths = audiencePath.replace(activePage.path, '').split('/').filter(Boolean);
  paths.forEach((path, index) => {
    let label = ROUTE_LABELS[path] || path;

    // If we have the audience name we use it here
    if (audience && audience.audience_name !== "" && index === 0) {
      label = audience.audience_name
    }

    breadcrumbs.push({title: label, path: `${activePage.path}/${paths.slice(0, index + 1).join('/')}`})
  })

  return breadcrumbs
}

/**
 * Filter a list of audiences using search terms, multiple filters and sort
 */
export const filterAudiences = (
  audiences: Audience[] | null,
  searchText?: string,
  projectFilter?: string,
  sortBy?: string,
  filterBy?: string,
  filterKey?: string,
) => {
  // Filter by search text
  let filteredAudiences = audiences?.filter((audience) => {
      if (audience.segment && searchText !== "") {
        return (audience.audience_name.toLowerCase().includes(searchText?.toLowerCase() || ""))
          || (audience.segments.some(x => x.segment_name.toLowerCase().includes(searchText?.toLowerCase() || "")))
      } else {
        return audience.audience_name
          .toLowerCase()
          .includes(searchText?.toLowerCase() || "")
      }
    }

  );

  // Filter by project
  if (projectFilter) {
    filteredAudiences = filteredAudiences?.filter(
      (audience) => audience.project_name === projectFilter
    );
  }

  // Apply filter
  if (filterBy && filterKey) {
    filteredAudiences = filteredAudiences?.filter(
      (audience) => audience[filterKey as keyof Audience] === filterBy
    );
  }

  // Apply sorting
  if (sortBy) {
    if (sortBy === "name") {
      filteredAudiences?.sort((a, b) =>
        a.audience_name.localeCompare(b.audience_name)
      );
    } else if (sortBy === "date") {
      filteredAudiences?.sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() -
          new Date(a.created_at || 0).getTime()
      );
    }
  } else {
    // Make sure the audiences with no project are always shown first unless there is a sort
    filteredAudiences = [
      ...(filteredAudiences?.filter((x) => x.project_name === null) || []),
      ...(filteredAudiences?.filter((x) => x.project_name !== null) || []),
    ];
  }

  return filteredAudiences;
};

export const checkAudienceSize = (summary: AudienceSummary) => {
  if (summary.num_postcodes !== undefined && summary.num_postcodes > 0) {
    return true;
  } else return summary.num_sectors !== undefined && summary.num_sectors > 0;
};


export const checkVariables = (audience: BuildAudience, variables: Variable[]) => {
  if (!variables || variables.length === 0) return true;

  const audienceVariables = audience.var_groups.map(x => x.variables).flat();
  const variableIds = new Set(variables.map(v => v.tag_id));
  return audienceVariables.every(v => variableIds.has(v.tag_id));
}

export const checkVariable = (selection: Variable, variables: Variable[]) => {
  return variables.map(v => v.tag_id).includes(selection.tag_id);
}

export const toAudienceType = (value: string): AudienceType | undefined => {
  const audienceTypes = ['build', 'upload', 'mindset'];
  if (audienceTypes.includes(value as AudienceType)) {
    return value as AudienceType
  } else {
    return undefined
  }
}

export const checkAudience = (audience: Audience): boolean => {

  const client = process.env.REACT_APP_CLIENT || 'core';

  return audience.client === client;
}

export const getAudienceIcon = (mode: string | undefined) => {
  const option = uploadModeOptions.find(x => x.value === mode)
  if (option) {
    const Component = option.iconComponent
    return (
      <HelpBox title={option.label}>
        <Component color="info" style={{ fontSize: 20 }}/>
      </HelpBox>
    )
  }
}

export const createBuildDefinition = (audience: BuildAudience) => {
  if (!audience) return '';

  const definition = {
    mode: audience.build_mode,
    op: audience.operator,
    groups: audience.var_groups.map((group) => ({
      op: group.operator,
      vars: group.variables.map((variable) => variable.tag_id).sort((a: number, b: number) => a - b),
    })),
    geo: audience.geo_fence ? audience.geo_fence.map(geo => geo.geo_id) : []
  }
  return JSON.stringify(definition);
}

export const createUploadDefinition = (audience: UploadAudience) => {
  if (!audience) return '';

  const definition = {
    mode: audience.upload_mode,
    travel_time: audience.travel_time,
    upload_file: audience.upload_file?.name
  }
  return JSON.stringify(definition);
}

export const extractSegments = (summary: AudienceSummary[]): AudienceSegment[] => {
  return summary.map((x, index) => ({
    segment_id: index + 1,
    segment_name: x.segment ?? `Segment ${index + 1}`,
  }));
};

const collapseToRanges = (selected: string[], masterOrder: string[]): string[] => {
  if (selected.length === 0) return [];
  if (selected.length === masterOrder.length) return ['All'];

  // 1. Get sorted indices of selected items based on master array order
  const indices = selected
    .map(item => masterOrder.indexOf(item))
    .filter(index => index !== -1)
    .sort((a, b) => a - b);

  const ranges: string[] = [];
  let start = indices[0];
  let prev = indices[0];

  // 2. Linear scan to find continuous blocks
  for (let i = 1; i <= indices.length; i++) {
    const current = indices[i];

    // If consecutive, keep extending the current range
    if (current === prev + 1) {
      prev = current;
    } else {
      // Range broken or reached end of array -> format the block
      if (start === prev) {
        // Single isolated bracket
        ranges.push(masterOrder[start]);
      } else {
        // Continuous block found. Merge the string bounds.
        const startString = masterOrder[start];
        const endString = masterOrder[prev];

        // Custom string parsing logic depending on format
        if (startString.includes('-') && endString.includes('-')) {
          // e.g., "18-24" + "25-34" -> "18-34"
          const lowerBound = startString.split('-')[0];
          const upperBound = endString.split('-')[1];
          ranges.push(`${lowerBound}-${upperBound}`);
        } else if (startString.startsWith('Under') && endString.includes('-')) {
          // e.g., "Under £20k" + "£20k-£30k" -> "Under £30k"
          const upperBound = endString.split('-')[1];
          ranges.push(`Under ${upperBound}`);
        } else if (startString.includes('-') && endString.endsWith('+')) {
          // e.g., "45-54" + "55+" -> "45+"
          const lowerBound = startString.split('-')[0];
          ranges.push(`${lowerBound}+`);
        } else {
          // Fallback if formatting doesn't stitch cleanly
          ranges.push(`${startString} to ${endString}`);
        }
      }
      // Reset markers for the next block
      start = current;
      prev = current;
    }
  }

  return ranges;
};

export const getDemographicSummary = (ages: string[], genders: string[], incomes: string[]): string => {
  if (ages.length === 0 && genders.length === 0 && incomes.length === 0) {
    return 'Anyone';
  }

  // Handle Ages
  let ageText = '';
  const ageRanges = collapseToRanges(ages, ageOptions);
  if (ageRanges.length > 0) {
    if (ageRanges[0] === 'All') {
      ageText = 'All ages';
    } else {
      // Stitches disjointed ranges elegantly: "18-34 and 45-54 year olds"
      const dynamicAgeString = ageRanges.reduce((acc, val, i) =>
        acc + (i === 0 ? '' : i === ageRanges.length - 1 ? ' and ' : ', ') + val, ''
      );
      ageText = `${dynamicAgeString} year olds`;
    }
  } else {
    ageText = 'People';
  }

  // Handle Genders
  const genderText = genders.length > 0
    ? (genders.length === 3 ? 'of any gender' : `who identify as ${genders.join('/')}`)
    : '';

  // Handle Incomes
  let incomeText = '';
  const incomeRanges = collapseToRanges(incomes, incomeOptions);
  if (incomeRanges.length > 0) {
    if (incomeRanges[0] === 'All') {
      incomeText = 'across all income brackets';
    } else {
      const dynamicIncomeString = incomeRanges.reduce((acc, val, i) =>
        acc + (i === 0 ? '' : i === incomeRanges.length - 1 ? ' or ' : ', ') + val, ''
      );
      incomeText = `who earn ${dynamicIncomeString}`;
    }
  }

  return [ageText, genderText, incomeText].filter(Boolean).join(' ');
};

export const getLocationSummary = (selected: GeoFenceLocation[]): string => {
  if (selected.length === 0) return 'living anywhere';

  const regions = selected.filter((l) => l.geo_type === 'Region').map((l) => l.geo_name);
  const postals = selected.filter((l) => l.geo_type === 'Postal Area').map((l) => l.geo_name);

  const clauses: string[] = [];
  if (regions.length > 0) {
    const regionStr = regions.reduce((acc, val, i) =>
      acc + (i === 0 ? '' : i === regions.length - 1 ? ' or ' : ', ') + val, ''
    );
    clauses.push(regionStr);
  }

  if (postals.length > 0) {
    const postalStr = postals.reduce((acc, val, i) =>
      acc + (i === 0 ? '' : i === postals.length - 1 ? ' or ' : ', ') + val, ''
    );
    clauses.push(`the ${postalStr} Postal Area${postals.length > 1 ? 's' : ''}`);
  }

  return `living in ${clauses.join(' or ')}`;
};

const parseGroupToClause = (group: VariableGroup): React.ReactNode => {
  if (group.variables.length === 0) return "";

  // Group variables by their category type to handle mixed cards gracefully
  const variablesByPrefix: Record<string, string[]> = {};
  group.variables.forEach((v) => {
    // Fallback gracefully to a baseline if prefix happens to be missing or blank
    const prefixKey = v.prefix?.trim() || `have ${v.tag_type} of`;

    if (!variablesByPrefix[prefixKey]) {
      variablesByPrefix[prefixKey] = [];
    }
    variablesByPrefix[prefixKey].push(v.tag_name);
  });

  const numPrefixes = Object.keys(variablesByPrefix).length;
  const inlineOperator = group.operator === "AND" ? "and" : "or";
  let operatorModifier = `${group.operator} of`;
  if (group.operator === "OR") operatorModifier = "any of";
  if (group.operator === "AND") operatorModifier = "all of";
  if (group.operator === "NOT") operatorModifier = "none of";
  if (group.operator === "SOME") operatorModifier = "some of";

  return (
    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', display: 'inline' }}>
      {Object.keys(variablesByPrefix).map((prefix, index) => {
        const names = variablesByPrefix[prefix];
        const nameList = names.reduce((a, n, i) => a + (i === 0 ? "" : i === names.length - 1 ? `, ${inlineOperator} ` : ", ") + n, "");
        const operator = (prefix.includes('of')) || (names.length === 1) ? "" : operatorModifier;

        return (
          <Box component="span" key={prefix}>
            <Box component="span" sx={{ color: 'info.main' }}>{prefix}</Box>
            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 'normal' }}>{` ${operator} `}</Box>
            <Box component="span" sx={{ color: 'secondary.main' }}>{nameList}</Box>
            {(numPrefixes > 1) && (index !== numPrefixes - 1) && <Box component="span" sx={{ color: 'info.main' }}> {inlineOperator} </Box>}
          </Box>
        );
      })}
    </Typography>
  );
};

export const getSelectionSummary = (
  variableGroups: VariableGroup[],
  operator: string
): React.ReactNode | React.ReactNode[] | null => {

  const validGroups = variableGroups.filter((g) => g.variables.length > 0);

  if (validGroups.length === 0) return null;

  let linesList = validGroups.map(parseGroupToClause);

  return linesList.reduce((acc: React.ReactNode[], currentLine, index) => {
    if (index > 0) {
      acc.push(
        <Typography component="span" variant="body2">
          {` ${operator} `}
        </Typography>
      );
    }
    acc.push(currentLine);
    return acc;
  }, []);
}

export const getFirstWord = (selectionSummary: React.ReactNode | React.ReactNode[]): string => {
  if (Array.isArray(selectionSummary)) {
    const firstNode =  selectionSummary[0];
    if (!React.isValidElement(firstNode)) return '';

    const prefix = String(firstNode.props.children[0].key || '').trim();
    return prefix.split(' ')[0]

  } else {
    return '';
  }
}
